import dayjs from "dayjs"
import * as yup from "yup"

export const PE_STATUSES = {
  toValidate: "WEIGHT_TO_VALIDATE",
  locked: "LOCKED",
  todo: "TODO",
  inProgress: "IN_PROGRESS",
  packagingsCounting: "PACKAGINGS_COUNTING",
  done: "DONE",
  waste: "WASTE_TO_WEIGHT",
  toTaste: "TO_TASTE"
}

export const PE_STATUSES_AND_LABELS = [
  {
    key: "WEIGHT_TO_VALIDATE",
    label: "Poids à valider"
  },
  {
    key: "LOCKED",
    label: "En attente"
  },
  {
    key: "TODO",
    label: "À démarrer"
  },
  {
    key: "IN_PROGRESS",
    label: "En cours"
  },
  {
    key: "PACKAGINGS_COUNTING",
    label: "Nbr de barquettes"
  },
  {
    key: "WASTE_TO_WEIGHT",
    label: "Waste réel à peser"
  },
  {
    key: "DONE",
    label: "Terminé"
  }
]

export const packagingParticularities = {
  nationalSend: "Envoi en province",
  lunchbag: "Lunchbag"
}

export const packagingParticularityOptions = [
  {
    value: "nationalSend",
    label: packagingParticularities.nationalSend
  },
  {
    value: "lunchbag",
    label: packagingParticularities.lunchbag
  }
]

export const getParticularitiesLabel = (pe) => {
  const particularities = []

  if (pe.nationalSend) {
    particularities.push(packagingParticularities.nationalSend)
  }

  if (pe.lunchbag) {
    particularities.push(packagingParticularities.lunchbag)
  }

  return particularities
}


export const formatPackagingExecutionsInitialValues = (packagingExecutions) => {
  const values = {}
  values.packagingExecutions = packagingExecutions.map((packagingExecution, index) => ({
    id: packagingExecution.objectId,
    theoreticalStartTime: packagingExecution.theoreticalStartTime ? dayjs(packagingExecution.theoreticalStartTime).format("HH:mm") : "",
    theoreticalEndTime: packagingExecution.theoreticalEndTime ? dayjs(packagingExecution.theoreticalEndTime).format("HH:mm") : "",
    recipe: packagingExecution.recipeName,
    code: packagingExecution.uniqueCode,
    packagingLine: packagingExecution.packagingLine ? packagingExecution.packagingLine.objectId : "",
    sectionsCount: packagingExecution.sections ? packagingExecution.sections.length : 0,
    expectedPackagingNumber: packagingExecution.expectedPackagingNumber || 0,
    particularity: getParticularitiesLabel(packagingExecution),
    status: PE_STATUSES_AND_LABELS.find(elem => elem.key === packagingExecution.status)?.label || "",
    comments: (packagingExecution.comments || []),
    // @important : use index of the non filtered list to keep track of the original position
    // even if the list is filtered
    index
  }))

  return values
}

export const formatPackagingExecutionWeightsInitialValues = (packagingExecution, proposedWeightsBySections, sectionOutputScales = {}, sectionOutputNames = {}) => {
  return {
    ...packagingExecution,
    packagings: packagingExecution.packagings.map(packaging => ({
      ...packaging,
      forecastNumber: packaging.forecastNumber || 0
    })),
    sections: packagingExecution.sections.map(section => {
      // e.g. 504 vs 504.00000000000006 should be considered equal
      const epsilon = 0.01
      const shouldNotUseProposedWeightFromData = Math.abs(section.productionWeight - section.totalTheoreticalWeight) < epsilon

      // scale: fraction of this section's production that stays here (not consumed by other sections)
      const scale = sectionOutputScales[section.section.objectId] ?? 1
      const outputName = sectionOutputNames[section.section.objectId]

      let proposedWeight = 0
      if (shouldNotUseProposedWeightFromData) {
        proposedWeight = (section.recipeSectionWeight || 0) * scale
      } else {
        proposedWeight = (proposedWeightsBySections[section.section.objectId] || 0) * scale
      }

      return {
        ...section,
        // Production quantities are scaled down to only what remains in this section
        productionWeight: section.productionWeight * scale,
        totalTheoreticalWeight: section.totalTheoreticalWeight * scale,
        recipeSectionWeight: (section.recipeSectionWeight || 0) * scale,
        initialProductionWeight: (section.initialProductionWeight || 0) * scale,
        forecastWaste: (section.forecastWaste || 0) * scale,
        // Per-barquette weights are NOT scaled — they don't change based on section outputs
        realWeight: 0,
        proposedWeight: proposedWeight,
        packagingForecastNumber: Infinity, // won't be saved in db for display only and to ease waste calculations
        reproductions: section.reproductions || [],
        sectionName: outputName ? `${section.sectionName} - ${outputName}` : section.sectionName,
      }
    }),
    packagingForecastNumber: 0 // won't be saved in db for display only and to ease waste calculations
  }
}

export const packagingExecutionWeightValidator = yup.object().shape({
  packagings: yup.array().of(
    yup.object().shape({
      forecastNumber: yup.number().min(0, "Le nombre de produits barquettés doit être positif")
    })
  ),
  sections: yup.array().of(
    yup.object().shape({
      realWeight: yup.number().min(0, "Le poids par barquette doit être positif"),
      forecastWaste: yup.number().test(
        "almost-zero-or-positive",
        "Le waste prévisionnel doit être nul ou positif",
        value => value === undefined || value >= -1e-8
      )
    })
  )
})

export const getPackagingsRealNumber = (packagings) => packagings.reduce((acc, packaging) => acc += (packaging.realNumber || 0), 0)
export const getPackagingsTheoreticalNumber = (packagings) => packagings.reduce((acc, packaging) => acc += packaging.theoreticalNumber, 0)

// Generate a unique list of filter options for a given field in packaging executions.
export const getPEsHoursEditUniqueOptions = (values, field, selectedSite, isPackagingLine = false) => {
  // Extract the array of packaging executions
  const executions = values?.packagingExecutions || []
  if (!Array.isArray(executions)) return []

  // Special case: fetch available packaging lines based on the selected site
  if (isPackagingLine) {
    return Array.from(
      new Set(getPEsHoursEditLineOptions(selectedSite).map(option => option.label))
    )
  }

  // Special case: recipe label is made of the recipe code and the recipe name
  if (field === "recipe") {
    return Array.from(
      new Set(
        executions.map(PE => `${PE.code} - ${PE.recipe}`)
      )
    ).filter(Boolean)
  }

  // Special case: get all the possible values, not the ones from the displayed PEs
  if (field === "particularity") return Object.values(packagingParticularities)

  // Default case: return unique non-empty values for the given field
  return Array.from(
    new Set(executions.map(PE => PE?.[field]))
  ).filter(Boolean)
}

// Filters packagingExecutions based on the current filter values
export const applyPEsHoursEditFilters = (values, filters, selectedSite, filterFields, getPEsHoursEditLineOptions) => {
  return values.packagingExecutions?.filter((PE) => {
    // Get the label (string) of the packaging line from its objectId
    const packagingLineLabel = getPEsHoursEditLineOptions(selectedSite)
      .find(line => line.value === PE.packagingLine)?.label || ""

    // Check that every filterField matches the current packaging execution (PE)
    return filterFields.every(({ key }) => {
      const val = filters[key]

      // If the filter is not defined or empty, it's considered a match (no filtering)
      if (!val || (Array.isArray(val) && val.length === 0)) return true

      // Special case: packaging line is stored as objectId, but we compare against label
      if (key === "packagingLine") {
        return Array.isArray(val)
          ? val.includes(packagingLineLabel)
          : packagingLineLabel.toLowerCase().includes(val.toLowerCase())
      }

      // Special case: recipe label is made of the recipe code and the recipe name
      if (key === "recipe") {
        const fullLabel = `${PE.code} - ${PE.recipe}`.toLowerCase()
        return fullLabel.includes(val.toString().toLowerCase())
      }

      // Special case: particularity is an array of strings — check if one matches
      if (key === "particularity") {
        return Array.isArray(PE.particularity)
          ? PE.particularity.some(p =>
            typeof val === "string"
              ? p.toLowerCase().includes(val.toLowerCase())
              : val.includes(p)
          )
          : false
      }

      // Default case: convert value to lowercase string and match as string or in array
      const fieldValue = PE[key]?.toString()?.toLowerCase?.()

      return Array.isArray(val)
        ? val.includes(PE[key])
        : fieldValue?.includes(val.toString().toLowerCase())
    })
  })
}

/**
 * Transforms a site's packagingLines into an array of { key, value, label } option objects.
 * This is used to populate dropdowns or filters where a user selects a packaging line.
 *
 * @param {Object} site - The site object containing the packagingLines array.
 * @returns {Array} - An array of option objects with shape: { key, value, label }.
 */
export const getPEsHoursEditLineOptions = (site) => {
  if (!site.packagingLines) return []

  return site.packagingLines.map(packagingLine => ({
    key: packagingLine.name,
    value: packagingLine.objectId,
    label: packagingLine.name
  }))
}

/**
 * Transform raw PackagingExecution.packagings into a brand/type breakdown.
 *
 * - Builds a packagingTypePerBrand map: brand → packaging type → count
 * - Computes totals per brand, per packaging type, and overall
 * - Detects the single active packaging type (first item with startTime)
 * - Filters out brands with no values
 *
 * @param {Array} packagings Raw packagings array from PackagingExecution
 * @param {Array<{name:string,image?:string}>} brands Brand list used for ordering and visibility
 * @param {Array<string>} headerOrder Packaging types to include in order (e.g. ["CAPPED","REUSABLE","DISPOSABLE"])
 * @returns {{
 *   packagingTypePerBrand: Record<string, Record<string, number>>,
 *   brandTotals: Record<string, number>,
 *   typeTotals: Record<string, number>,
 *   total: number,
 *   activeType: string | null,
 *   visibleBrands: Array<{name:string,image?:string}>
 * }}
 */

export const formatPackagingsByBrandAndType = (packagings, brands, headerOrder, statusDone) => {
  const initialTypeCounts = Object.fromEntries(headerOrder.map(t => [t, { real: 0, theoretical: 0 }]))

  const res = packagings.reduce((res, packaging) => {
    // find active type when not done
    if (!statusDone && packaging.startTime && !packaging.endTime && res.activeType === null) res.activeType = packaging.type

    // Browse through each brand objects
    let theoSum = 0
    const brands = packaging.brands || []
    brands.forEach(brand => { theoSum = theoSum + brand.theoreticalNumber })

    brands.forEach(brand => {
      const name = brand.brand
      const theo = brand.theoreticalNumber

      let real = 0
      if (theoSum > 0) real = packaging.realNumber * (theo / theoSum)

      // init brand row (deep-ish clone of the per-type template)
      if (!res.packagingTypePerBrand[name]) {
        res.packagingTypePerBrand[name] = Object.fromEntries(
          Object.keys(initialTypeCounts).map(t => [t, { ...initialTypeCounts[t] }])
        )
      }

      // init name total
      if (!res.brandTotals[name]) res.brandTotals[name] = { real: 0, theoretical: 0 }

      // per name & type
      res.packagingTypePerBrand[name][packaging.type].theoretical = res.packagingTypePerBrand[name][packaging.type].theoretical + theo
      res.packagingTypePerBrand[name][packaging.type].real = res.packagingTypePerBrand[name][packaging.type].real + real

      // per brand totals
      res.brandTotals[name].theoretical = res.brandTotals[name].theoretical + theo
      res.brandTotals[name].real = res.brandTotals[name].real + real

      // per type totals
      res.typeTotals[packaging.type].theoretical = res.typeTotals[packaging.type].theoretical + theo
      res.typeTotals[packaging.type].real = res.typeTotals[packaging.type].real + real

      // overall totals
      res.total.theoretical = res.total.theoretical + theo
      res.total.real = res.total.real + real
    })

    return res
  }, {
    packagingTypePerBrand: {},
    brandTotals: {},
    typeTotals: Object.fromEntries(headerOrder.map(t => [t, { real: 0, theoretical: 0 }])),
    total: { real: 0, theoretical: 0 },
    activeType: null
  })

  // when done, highlight the Total column
  if (statusDone) res.activeType = "total"
  // if everything has an endTime, also highlight Total
  if (!statusDone && packagings.length > 0) {
    const allEnded = packagings.every(pkg => pkg.endTime)
    if (allEnded) res.activeType = "total"
  }

  const visibleBrands = brands.filter(brand => {
    const t = res.brandTotals[brand.name]
    if (!t) return false
    return (t.theoretical || 0) > 0 || (t.real || 0) > 0
  })

  return { ...res, visibleBrands }
}